/**
 * Editor surface for curating an article's related-article links.
 *
 * Sits in `ArticleEditor` below `EmbeddedQueriesList`. Mirrors the
 * embed editor's pattern: self-managed local state, immediate
 * save-on-action, silent refetch (no flicker), optimistic local
 * updates. Mutations don't flow through `useArticleEditor`'s reducer,
 * so the Save button doesn't light up on related-article changes.
 *
 * The autocomplete source is the article index already loaded by
 * `ResearchLayout` — no extra fetch. We filter out:
 *   - the current article (no self-links — the backend rejects them
 *     too, but we keep them out of the picker so the user can't try)
 *   - articles already linked (no duplicates)
 *
 * Initial related list comes from the article detail payload (fetched
 * once on mount via `getResearchArticle`). After every successful
 * mutation we refetch the article to get a fresh list. This is cheap
 * because the editor is the only caller and we throttle by debouncing
 * the saved-changes flag.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useLeagueApi } from "../../../../data/LeagueEnvironmentProvider";
import {
  ResearchArticleIndexItem,
  ResearchRelatedRef,
} from "../../../../models/Research";
import { useResearchLayoutContext } from "../ResearchLayout";

export interface RelatedArticlesEditorProps {
  slug: string;
}

export default function RelatedArticlesEditor({
  slug,
}: RelatedArticlesEditorProps) {
  const api = useLeagueApi();
  const { articles: indexArticles } = useResearchLayoutContext();
  const [related, setRelated] = useState<ResearchRelatedRef[]>([]);
  // `initialLoading` only flips false after the first successful fetch
  // — see EmbeddedQueriesList for the same "silent refetch" pattern.
  const [initialLoading, setInitialLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Autocomplete's selected value is its own piece of state; we keep it
  // controlled so we can clear it back to null after picking.
  const [picker, setPicker] =
    useState<ResearchArticleIndexItem | null>(null);

  const refetch = React.useCallback(() => {
    let cancelled = false;
    api
      .getResearchArticle(slug)
      .then((resp) => {
        if (cancelled) return;
        setRelated(resp.data.related_articles ?? []);
        setInitialLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load related articles";
        setError(msg);
        setInitialLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, slug]);

  useEffect(() => {
    return refetch();
  }, [refetch]);

  // Picker source: every article in the index EXCEPT the current one
  // and any already-linked targets. The article index already respects
  // the caller's visibility (backend-filtered), so we don't need to
  // worry about leaking drafts.
  const pickerOptions = useMemo(() => {
    const linkedSlugs = new Set(related.map((r) => r.slug));
    return indexArticles.filter(
      (a) => a.slug !== slug && !linkedSlugs.has(a.slug),
    );
  }, [indexArticles, related, slug]);

  const handleAdd = async (target: ResearchArticleIndexItem | null) => {
    if (!target) return;
    setPicker(null); // clear the autocomplete selection eagerly
    setBusy(true);
    setError(null);
    // Optimistic: append a partial ref so the new row appears at the
    // bottom immediately. Refetch fills in the canonical shape.
    const optimistic: ResearchRelatedRef = {
      slug: target.slug,
      title: target.title,
      summary: target.summary,
      category: target.category,
    };
    setRelated((prev) => [...prev, optimistic]);
    try {
      await api.addResearchRelatedArticle(slug, target.slug);
      refetch();
    } catch (err: any) {
      refetch(); // revert
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to add related article",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (relatedSlug: string) => {
    // Optimistic local removal so the row disappears immediately.
    setRelated((prev) => prev.filter((r) => r.slug !== relatedSlug));
    setBusy(true);
    setError(null);
    try {
      await api.removeResearchRelatedArticle(slug, relatedSlug);
      refetch();
    } catch (err: any) {
      refetch();
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to remove related article",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fafafa",
        border: "1px solid #e6e6e6",
        borderRadius: "4px",
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: "text.secondary",
          mb: 1.5,
        }}
      >
        Related articles
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1.5, fontSize: "0.85rem" }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Autocomplete
        size="small"
        value={picker}
        onChange={(_e, v) => handleAdd(v)}
        options={pickerOptions}
        getOptionLabel={(o) => o.title}
        disabled={busy}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontSize: "0.9rem", fontWeight: 600 }}
              >
                {option.title}
              </Typography>
              {option.category && (
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  {option.category.name}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              pickerOptions.length === 0
                ? "No other articles available"
                : "Link a related article…"
            }
            sx={{
              "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
            }}
          />
        )}
        sx={{ mb: 1.5 }}
      />

      {initialLoading ? (
        <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
          Loading…
        </Typography>
      ) : related.length === 0 ? (
        <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
          No related articles linked yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {related.map((r) => (
            <Box
              key={r.slug}
              sx={{
                backgroundColor: "#fff",
                border: "1px solid #e6e6e6",
                borderRadius: "3px",
                px: 1.25,
                py: 0.75,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {r.title}
                  </Typography>
                  {r.category && (
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      {r.category.name}
                    </Typography>
                  )}
                </Box>
                <Tooltip title="Remove" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(r.slug)}
                      disabled={busy}
                      sx={{ color: "#CF0A2C" }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
